/**
 * Await an arche's `applyAction` with a timeout — the ui_action apply half of
 * the assistant loop's client side, extracted so the contract is unit-testable
 * without the full agent-chat harness.
 *
 * `applyAction` may be async: a plugin arche relays the action into its
 * sandboxed iframe and settles on the plugin's result. The caller must NOT
 * report success the instant the call doesn't throw — that is survivable for
 * one action but wrong for a loop ("do X to each of these 12 files"), where
 * the model would march on through failures believing they succeeded. Sync
 * implementations (internal arches) resolve immediately, exactly as before.
 */

/**
 * How long an arche's `applyAction` may run before the ui_action is reported
 * failed. Generous — a plugin may do real work (e.g. re-rendering pages) —
 * and consequence-free for sync implementations. The paused turn is durable
 * server-side, so nothing times out under us while we wait.
 */
export const UI_ACTION_APPLY_TIMEOUT_MS = 30_000;

export interface UiActionApplyResult {
  ok: boolean;
  /** Model-reactable failure description (absent on success). */
  error?: string;
}

export async function applyUiActionWithTimeout(
  applyAction: (actionId: string, params: Record<string, unknown>) => void | Promise<unknown>,
  actionId: string,
  params: Record<string, unknown>,
  timeoutMs: number = UI_ACTION_APPLY_TIMEOUT_MS,
): Promise<UiActionApplyResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // Promise.resolve() folds sync returns (void) and async results into
      // one awaitable; a sync THROW rejects through the same path.
      Promise.resolve(applyAction(actionId, params)),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`action did not complete within ${timeoutMs}ms`)),
          timeoutMs,
        );
      }),
    ]);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: `applyAction failed: ${String(err)}` };
  } finally {
    clearTimeout(timer);
  }
}
